import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatApiError } from '../lib/format';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      login(data.token, data.user);
      toast.success('Welcome back');
      const dest = location.state?.from || (data.user.role === 'admin' ? '/admin' : '/dashboard');
      navigate(dest);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="card-embossed p-8 sm:p-10">
        <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">Welcome back</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-black text-gold-gradient">Login</h1>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              placeholder="you@example.co.za"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="btn-gold w-full inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            Login
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <Link to="/forgot-password" className="text-[#C0C0C0] hover:text-[#D4AF37]">Forgot password?</Link>
          <Link to="/signup" data-testid="login-to-signup" className="text-[#D4AF37]">Create account</Link>
        </div>
      </div>
    </div>
  );
}
